import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row, Spinner, Stack } from 'react-bootstrap'
import './MFATab.scss'
import { Button } from '../../components/Button/Button'
import { MFAModal } from '../MFA/MFAModal'
import { type MFAFactor, type MFAFactorType, MFAModalPageIdentifier, type SMSMFAFactor, type TOTPMFAFactor } from '../../models/MFA'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useListFactorsQuery } from '../../store/mfa/mfa'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { useZocksAuth } from '../../components/ScopedBlock/hooks'
import { createMFA, deleteMFA, resetFlow, selectMFAFlow, selectPageIdentifier, updateMFA } from '../../store/mfa/mfaSlice'
import { formatPhoneNumber } from 'react-phone-number-input'
import { addToast } from '../../store/toast/toastSlice'

type VerificationMethodBoxProps = {
  title: string
  description: React.ReactNode
  factor?: MFAFactor
  onSetup: () => void
  onUpdate: (factor: MFAFactor) => void
  onDelete: (factor: MFAFactor) => void
  iconClassName: string
  loading: boolean
}

const VerificationMethodBox: React.FC<VerificationMethodBoxProps> = ({ title, description, factor, onSetup, onUpdate, onDelete, iconClassName, loading }) => {
  const { hasScope } = useZocksAuth()
  const hasEditScope = hasScope('**', 'mfa:update', ['mfa:admin'])
  const hasDeleteScope = hasScope('**', 'mfa:delete', ['mfa:admin'])

  const options = useMemo(() => {
    const opts = [{ id: 'delete', label: 'Remove', disabled: !hasDeleteScope }]
    if (factor?.type === 'sms') {
      opts.unshift({ id: 'edit', label: 'Edit', disabled: !hasEditScope })
    }
    return opts
  }, [hasDeleteScope, hasEditScope, factor?.type])

  const handleOptionSelected = async (selectOption: SelectOption) => {
    if (factor) {
      const { id } = selectOption
      switch (id) {
        case 'edit':
          onUpdate(factor)
          return
        case 'delete':
          onDelete(factor)
          return
      }
    }
  }

  return (
    <Container>
      <Row className={`${factor ? 'settings-verification-method-box-enabled' : 'settings-verification-method-box-disabled'} mx-0 my-2`}>
        <Col xs='auto' className='d-flex flex-column justify-content-center p-0'>
          <div
            className={`${factor ? 'settings-verification-method-icon-box-enabled' : 'settings-verification-method-icon-box-disabled'} d-flex justify-content-center align-items-center`}
          >
            <i className={`${factor ? 'settings-verification-method-icon-enabled' : 'settings-verification-method-icon-disabled'} bi ${iconClassName}`} />
          </div>
        </Col>
        <Col xs={10} className='d-grid gap-2 zocks-px-1.25'>
          <Stack direction='horizontal' gap={2}>
            <div className='lead-medium text-uppercase'>{title}</div>
            {factor ? <i className='bi bi-check-lg zocks-text-primary' /> : null}
          </Stack>
          <div>{description}</div>
        </Col>
        <Col xs={{ order: 'last' }} className='d-flex flex-column justify-content-center align-items-end p-0'>
          {loading ? (
            <Spinner size='sm' />
          ) : factor ? (
            <div className='mfa-dropdown-container d-none d-sm-block'>
              <SelectDropdown
                className='zocks-py-0.375 zocks-px-0.75'
                options={options}
                icon='bi-three-dots-vertical'
                iconOnly
                hideArrows
                onOptionSelected={handleOptionSelected}
              />
            </div>
          ) : (
            <Button variant='primary' onClick={onSetup}>
              Set up
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  )
}

type VerificationMethodBoxSMSProps = {
  onSetup: () => void
  onUpdate: (factor: MFAFactor) => void
  onDelete: (factor: MFAFactor) => void
  factor?: SMSMFAFactor
  loading: boolean
}

const VerificationMethodBoxSMS: React.FC<VerificationMethodBoxSMSProps> = ({ onSetup, onUpdate, onDelete, factor, loading }) => {
  return (
    <VerificationMethodBox
      title='Text message'
      description={
        factor ? (
          <>
            Receive a verification code by text. You’ll need phone signal for this.{' '}
            <strong>Your validated number: {formatPhoneNumber(factor.phoneNumber)}</strong>
          </>
        ) : (
          'Receive a verification code by text. You’ll need phone signal for this.'
        )
      }
      factor={factor}
      onSetup={onSetup}
      onUpdate={onUpdate}
      onDelete={onDelete}
      iconClassName='bi-chat-square-text'
      loading={loading}
    />
  )
}

type VerificationMethodBoxTOTPProps = {
  onSetup: () => void
  onUpdate: (factor: MFAFactor) => void
  onDelete: (factor: MFAFactor) => void
  factor?: TOTPMFAFactor
  loading: boolean
}

const VerificationMethodBoxTOTP: React.FC<VerificationMethodBoxTOTPProps> = ({ onSetup, onUpdate, onDelete, factor, loading }) => {
  return (
    <VerificationMethodBox
      title='Authenticator app'
      description='Verify using a separate authenticator app. No need to wait for a text, and you just need an internet connection. Our recommended app is Google Authenticator.'
      factor={factor}
      onSetup={onSetup}
      onUpdate={onUpdate}
      onDelete={onDelete}
      iconClassName='bi-app-indicator'
      loading={loading}
    />
  )
}

export const MFATab: React.FC = () => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const flow = useAppSelector(selectMFAFlow)
  const pageIdentifier = useAppSelector(selectPageIdentifier)
  const { data: factors, isFetching } = useListFactorsQuery({ accountId: accountId })
  const [factorSetup, setFactorSetup] = useState<MFAFactorType | null>(null)
  const [showModal, setShowModal] = useState<boolean>(false)

  useEffect(() => {
    if (factorSetup) {
      dispatch(createMFA(factorSetup))
    }
  }, [factorSetup])

  useEffect(() => {
    setShowModal(!!factorSetup)
  }, [factorSetup])

  const findType = useCallback(
    (type: MFAFactorType): MFAFactor | undefined => {
      return factors?.find((factor) => factor.type === type)
    },
    [factors],
  )

  const setupFactor = (type: MFAFactorType) => {
    dispatch(resetFlow())
    setFactorSetup(type)
  }

  const updateFactor = (factor: MFAFactor) => {
    dispatch(resetFlow())
    dispatch(updateMFA(factor))
    setShowModal(true)
  }

  const deleteFactor = (factor: MFAFactor) => {
    dispatch(resetFlow())
    dispatch(deleteMFA(factor.id))
    setShowModal(true)
  }

  const onHide = () => {
    if (pageIdentifier === MFAModalPageIdentifier.SETUP_COMPLETE) {
      const message =
        flow === 'create' ? 'The authentication method has been successfully created.' : 'The authentication method has been successfully updated.'
      dispatch(addToast({ variant: 'success', title: '2-factor authentication', messages: [message] }))
    }
    setFactorSetup(null)
    setShowModal(false)
    dispatch(resetFlow())
  }

  const onSuccess = () => {
    switch (flow) {
      case 'delete':
        onHide()
        dispatch(addToast({ variant: 'success', title: '2-factor authentication', messages: ['The authentication method has been successfully removed.'] }))
        break
    }
  }

  return (
    <>
      <MFAModal show={showModal} onHide={onHide} loading={false} onSuccess={onSuccess} />
      <Container>
        <Row className='pb-1'>
          <h5>2-factor authentication</h5>
        </Row>
        <Row className='py-3'>
          <div className='paragraph-medium'>Add an extra layer of security to your account in case your password is compromised.</div>
        </Row>
        <Row className='p-3 m-0 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary' />
              <span className='zocks-text-gray-900'>Please make sure you update to the latest version of our mobile app to use 2-factor authentication.</span>
            </div>
          </Col>
        </Row>
        <Row className='pt-5 pb-2'>
          <h6>Choose your verification methods</h6>
        </Row>
        <Row>
          <VerificationMethodBoxSMS
            factor={findType('sms') as SMSMFAFactor}
            onSetup={() => setupFactor('sms')}
            onUpdate={updateFactor}
            onDelete={deleteFactor}
            loading={isFetching}
          />
        </Row>
        <Row>
          <VerificationMethodBoxTOTP
            factor={findType('totp') as TOTPMFAFactor}
            onSetup={() => setupFactor('totp')}
            onUpdate={updateFactor}
            onDelete={deleteFactor}
            loading={isFetching}
          />
        </Row>
      </Container>
    </>
  )
}
